import { useNavigate } from '@tanstack/react-router'
import { useEffect, useId, useMemo, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { ActivePill } from '@/components/ui/glide'
import { Icon } from '@/components/ui/icon'
import { useAccess } from '@/hooks/queries/access'
import { useTickets } from '@/hooks/queries/tickets'
import { cn } from '@/lib/utils'

import { allPages } from './navigation'

/** How long typing rests before the tickets are asked for. */
const DEBOUNCE_MS = 250
/** Tickets are searched from this many characters; one letter matches everything. */
const MIN_TICKET_QUERY = 2
const TICKET_LIMIT = 5

type Group = 'actions' | 'pages' | 'tickets'

type Item = {
  id: string
  group: Group
  label: string
  /** Where it sits, muted after the label: a page's folders, a ticket's type. */
  detail?: string
  icon: string
  hue?: string
  /** A ticket number, set in mono before the label. */
  code?: string
  go: () => void
}

/** ⌘ on a Mac, Ctrl everywhere else — the key the shortcut actually uses. */
function shortcutKey(): string {
  try {
    return /Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘' : 'Ctrl'
  } catch {
    return 'Ctrl'
  }
}

/**
 * The command menu (after Untitled UI's): ⌘K or Ctrl+K from anywhere, or the
 * search field in the header. Type to find any page in the sidebar's map —
 * by its name or the folders it sits in — or a ticket by its number, SKU or
 * words; Enter opens it.
 *
 * Its open state is not in the URL: it is a launcher, not a view of anything,
 * and the back button closing it would undo nothing.
 *
 * `onAskAi`, when given (the reader can use the AI Analyst), adds "Ask AI
 * about this page" to the actions, which opens the docked analyst panel.
 */
export function CommandMenu({ onAskAi }: { onAskAi?: () => void } = {}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const key = useMemo(shortcutKey, [])

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        setOpen((previous) => !previous)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-keyshortcuts="Meta+K Control+K"
        aria-label={t('search.open')}
        className="border-border bg-surface text-muted-foreground hover:bg-surface-hover hover:text-foreground flex h-9 items-center gap-2 rounded-md border px-2.5 text-sm transition-colors md:w-80 lg:w-96"
      >
        <Icon name="search" size={16} />
        <span className="hidden flex-1 text-left md:block">{t('search.placeholderShort')}</span>
        <kbd className="bg-surface-muted text-2xs hidden rounded-sm border px-1.5 py-0.5 font-sans font-medium md:block">
          {key} K
        </kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          showCloseButton={false}
          className="top-[12vh] translate-y-0 gap-0 overflow-hidden p-0 sm:max-w-140"
        >
          <DialogTitle className="sr-only">{t('search.title')}</DialogTitle>
          <DialogDescription className="sr-only">{t('search.description')}</DialogDescription>
          {/* Mounted only while open, so every opening starts from an empty query. */}
          {open ? <Palette onClose={() => setOpen(false)} onAskAi={onAskAi} /> : null}
        </DialogContent>
      </Dialog>
    </>
  )
}

function Palette({ onClose, onAskAi }: { onClose: () => void; onAskAi?: () => void }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const listId = useId()
  const [query, setQuery] = useState('')
  const [settled, setSettled] = useState('')
  const [active, setActive] = useState(0)

  useEffect(() => {
    const timer = setTimeout(() => setSettled(query.trim()), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [query])

  const searchTickets = settled.length >= MIN_TICKET_QUERY
  const tickets = useTickets({ q: settled, limit: TICKET_LIMIT }, searchTickets)

  const access = useAccess().data
  const pages = useMemo(
    () =>
      allPages(access).map((page) => ({
        page,
        label: t(page.key),
        detail: page.trail.map((key) => t(key)).join(' / '),
      })),
    [t, access],
  )

  const needle = query.trim().toLowerCase()
  const go = (action: () => void) => () => {
    onClose()
    action()
  }

  const items: Item[] = [
    ...(needle === '' || t('search.raise').toLowerCase().includes(needle)
      ? [
          {
            id: 'raise',
            group: 'actions' as const,
            label: t('search.raise'),
            icon: 'add_circle',
            hue: 'text-primary',
            go: go(() => void navigate({ to: '/tickets/new' })),
          },
        ]
      : []),
    ...(onAskAi &&
    (needle === '' || t('aiAnalyst.shell.launcher.askAiAboutPage').toLowerCase().includes(needle))
      ? [
          {
            id: 'ask-ai',
            group: 'actions' as const,
            label: t('aiAnalyst.shell.launcher.askAiAboutPage'),
            icon: 'auto_awesome',
            hue: 'text-primary',
            go: go(onAskAi),
          },
        ]
      : []),
    ...pages
      .filter(
        ({ label, detail }) =>
          needle === '' ||
          label.toLowerCase().includes(needle) ||
          detail.toLowerCase().includes(needle),
      )
      .map(({ page, label, detail }) => ({
        id: `page:${page.to}`,
        group: 'pages' as const,
        label,
        detail: detail || undefined,
        icon: page.icon,
        hue: page.hue,
        go: go(() => void navigate({ to: page.to })),
      })),
    ...(searchTickets && tickets.data && settled === query.trim()
      ? tickets.data.items.map((ticket) => ({
          id: `ticket:${ticket.id}`,
          group: 'tickets' as const,
          code: `#${String(ticket.number)}`,
          label: ticket.ticket_type_name,
          detail: ticket.store_name ?? undefined,
          icon: 'confirmation_number',
          hue: 'text-nav-tickets',
          go: go(
            () => void navigate({ to: '/tickets/$ticketId', params: { ticketId: ticket.id } }),
          ),
        }))
      : []),
  ]

  const current = Math.min(active, Math.max(items.length - 1, 0))
  const optionId = (index: number) => `${listId}-${String(index)}`

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (items.length === 0) return
      const step = event.key === 'ArrowDown' ? 1 : -1
      const next = (current + step + items.length) % items.length
      setActive(next)
      document.getElementById(optionId(next))?.scrollIntoView({ block: 'nearest' })
    } else if (event.key === 'Enter') {
      event.preventDefault()
      items[current]?.go()
    }
  }

  const groups: Group[] = ['actions', 'pages', 'tickets']
  const pendingTickets = searchTickets && (tickets.isFetching || settled !== query.trim())

  return (
    <div className="flex max-h-[min(70vh,34rem)] flex-col max-sm:max-h-dvh">
      <div className="flex h-14 shrink-0 items-center gap-3 border-b px-4">
        <Icon name="search" className="text-muted-foreground" />
        <input
          autoFocus
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={items.length > 0 ? optionId(current) : undefined}
          aria-label={t('search.title')}
          placeholder={t('search.placeholder')}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value)
            setActive(0)
          }}
          onKeyDown={onKeyDown}
          className="placeholder:text-muted-foreground min-w-0 flex-1 bg-transparent text-base outline-none"
        />
        {pendingTickets ? (
          <span className="text-muted-foreground text-xs">{t('search.searching')}</span>
        ) : null}
        <kbd className="bg-surface-muted text-2xs text-muted-foreground rounded-sm border px-1.5 py-0.5 font-sans font-medium">
          esc
        </kbd>
      </div>

      <div
        id={listId}
        role="listbox"
        aria-label={t('search.results')}
        className="min-h-0 flex-1 scrollbar-none overflow-y-auto p-2"
      >
        {items.length === 0 && !pendingTickets ? (
          <p className="text-muted-foreground px-3 py-10 text-center text-sm">
            {t('search.empty', { query: query.trim() })}
          </p>
        ) : null}
        {groups.map((group) => {
          const members = items
            .map((item, index) => ({ item, index }))
            .filter(({ item }) => item.group === group)
          if (members.length === 0) return null
          return (
            <div key={group} role="group" aria-label={t(`search.groups.${group}`)} className="pb-2">
              <p
                aria-hidden="true"
                className="text-muted-foreground tracking-overline text-2xs px-3 pt-2 pb-1.5 font-bold uppercase"
              >
                {t(`search.groups.${group}`)}
              </p>
              {members.map(({ item, index }) => (
                <div
                  key={item.id}
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === current}
                  onPointerMove={() => setActive(index)}
                  onClick={item.go}
                  className={cn(
                    'relative flex h-10 cursor-pointer items-center gap-3 rounded-md px-3 text-sm',
                    index === current ? 'text-foreground' : 'text-foreground-secondary',
                  )}
                >
                  {index === current ? (
                    <ActivePill layoutId="command-active" className="bg-surface-muted rounded-md" />
                  ) : null}
                  <Icon name={item.icon} size={16} className={cn('relative', item.hue)} />
                  {item.code ? (
                    <span className="text-primary-text relative font-mono text-xs font-semibold">
                      {item.code}
                    </span>
                  ) : null}
                  <span className="relative truncate font-medium">{item.label}</span>
                  {item.detail ? (
                    <span className="text-muted-foreground relative min-w-0 truncate text-xs">
                      {item.detail}
                    </span>
                  ) : null}
                  {index === current ? (
                    <Icon
                      name="keyboard_return"
                      size={16}
                      className="text-muted-foreground relative ml-auto"
                    />
                  ) : null}
                </div>
              ))}
            </div>
          )
        })}
        {searchTickets && tickets.isError ? (
          <p className="text-muted-foreground px-3 py-2 text-xs">{t('search.ticketsFailed')}</p>
        ) : null}
      </div>

      <div className="text-muted-foreground text-2xs hidden shrink-0 items-center gap-4 border-t px-4 py-2 sm:flex">
        <span>{t('search.hintMove')}</span>
        <span>{t('search.hintOpen')}</span>
        <span>{t('search.hintClose')}</span>
      </div>
    </div>
  )
}
